/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { EMPTY } from '@msdyn365-commerce/retail-proxy';
import { KeyCodes } from './KeyCodes';

export interface ILocalizedPrice {
    customizedAmount?: string;
    amountVariable?: string;
    paymentAmount?: number;
}

/**
 * Get Locale Based Customized Price function.
 * @param customPrice - customPrice.
 * @param state - state.
 * @param locale - locale.
 * @param keyCode - keyCode
 * @returns modified state.
 */
export function getLocaleBasedCustomizedPrice(
    customPrice: string,
    state: ILocalizedPrice,
    locale: string,
    keyCode: number
): ILocalizedPrice {
    // Checking if the backspace is being pressed then remove the particular digit from string. so added char 'B' to identify backspace event.
    const returnState: ILocalizedPrice = state;
    if (keyCode === KeyCodes.Back) {
        // Checking if the all value is being deleted by CTRL+ A+ BACKSPACE, then set value as 0.
        const amountData = (state.amountVariable as string).slice(0, -1);
        if (StringExtensions.isNullOrEmpty(customPrice)) {
            returnState.paymentAmount = parseFloat(customPrice);
            setLocaleBasedAmount('0', locale, returnState, state, keyCode);
        } else {
            // Otherwise remove the particular digit from string and send data to format.
            returnState.paymentAmount = parseFloat(amountData);
            setLocaleBasedAmount(customPrice, locale, returnState, state, keyCode);
        }
    } else if (StringExtensions.isNullOrWhitespace(customPrice) || customPrice.length === 1) {
        // Checking if the last entered digit is space only or contains only one digit then assign value accordingly without appending prefix value.
        returnState.paymentAmount = parseFloat(customPrice);
        if (customPrice.length === 1) {
            setLocaleBasedAmount(customPrice, locale, returnState, state, keyCode);
        } else {
            // If no value found then set value as 0.
            setLocaleBasedAmount('0', locale, returnState, state, keyCode);
        }
        // this._updatePrice(state.productPrice, parseFloat(customPrice));
    } else if (customPrice.charAt(customPrice.length - 1) === '.' && (state.amountVariable as string).split('.').length - 1 === 1) {
        // Checking if the last entered value is dot and existing value is containing dot or not.
        // checking for first char after dot should not contain any other dots.
        if (state.amountVariable?.includes('.') && customPrice.charAt(customPrice.length - 1) === '.') {
            return state;
        }
        let stringAmount = customPrice.replace(/[^0-9]/g, EMPTY);
        stringAmount = `${stringAmount}.`;
        returnState.paymentAmount = parseFloat(stringAmount);
        setLocaleBasedAmount(customPrice, locale, returnState, state, keyCode);
    } else if (customPrice.charAt(customPrice.length - 2) === '.' && (state.amountVariable as string).split('.').length - 1 === 1) {
        // checking for second char after dot should not contain any other dots.
        if (state.amountVariable?.includes('.') && customPrice.charAt(customPrice.length - 1) === '.') {
            return state;
        }
        returnState.paymentAmount = parseFloat(customPrice);
        setLocaleBasedAmount(customPrice, locale, returnState, state, keyCode);
    } else if ((state.amountVariable as string).includes('.') && (state.amountVariable as string).split('.').length - 1 === 1) {
        // checking if pre existing amount doesn't contain any dot then append last entered value and then send for customize the amount.
        const lastDigit = customPrice.charAt(customPrice.length - 1);
        returnState.paymentAmount = parseFloat((state.amountVariable as string) + lastDigit);
        setLocaleBasedAmount((state.amountVariable as string) + lastDigit, locale, returnState, state, keyCode);
    } else {
        // Generally without backspace and dot value comes under this section and get appended into existing string for further amount customization.
        const stringAmount = customPrice.replace(/[^0-9]/g, EMPTY && customPrice.split('.').length - 1 === 1);
        returnState.paymentAmount = parseFloat(stringAmount);
        setLocaleBasedAmount(customPrice, locale, returnState, state, keyCode);
    }

    return returnState;
}

/**
 * Set customizedAmount and amountVariable in state as per Locale Based function.
 * @param inputTextValue - inputTextValue.
 * @param locale - locale.
 * @param returnState - returnState.
 * @param state - state.
 * @param keyCode - keyCode.
 * @returns void.
 */
const setLocaleBasedAmount = (
    inputTextValue: string,
    locale: string,
    returnState: ILocalizedPrice,
    state: ILocalizedPrice,
    keyCode: number
): void => {
    let amountForUI: string = EMPTY;
    let newVar: string = (state.paymentAmount as number).toString();
    const usedVar = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

    // Check if the Input is one or less than one characters the initialize the value in input box.
    if (StringExtensions.isNullOrWhitespace(inputTextValue) || inputTextValue.length === 1) {
        const recentInputNumber = inputTextValue.charAt(inputTextValue.length - 1);
        if (usedVar.includes(recentInputNumber)) {
            returnState.amountVariable = inputTextValue;
        } else {
            return;
        }
        amountForUI = new Intl.NumberFormat(locale).format(Number(inputTextValue));
        returnState.customizedAmount = amountForUI;
    } else {
        const recentInputNumber = inputTextValue.charAt(inputTextValue.length - 1);

        // Append last input amount digit and append with previous input amount and then send to format the value.
        if (usedVar.includes(recentInputNumber)) {
            if (state.amountVariable?.includes('.')) {
                if (state.amountVariable?.substring(state.amountVariable?.indexOf('.') + 1).length <= 3) {
                    newVar = newVar.slice(0, -1);
                    returnState.amountVariable = newVar + recentInputNumber;
                    amountForUI = new Intl.NumberFormat(locale).format(Number(newVar + recentInputNumber));
                    returnState.customizedAmount = amountForUI;
                }
            } else {
                returnState.amountVariable = newVar;
                amountForUI = new Intl.NumberFormat(locale).format(Number(newVar));
                returnState.customizedAmount = amountForUI;
            }
        } else if (recentInputNumber === '.' && newVar.replace(/[^.]/g, EMPTY).length === 0 && newVar.split('.').length - 1 === 0) {
            // Append dot (.) in Amount string.
            const inputWithDot = newVar + recentInputNumber;
            returnState.amountVariable = inputWithDot;
            returnState.customizedAmount = inputWithDot;
        } else if (keyCode === KeyCodes.Back) {
            returnState.amountVariable = newVar;
            amountForUI = new Intl.NumberFormat(locale).format(Number(newVar));
            returnState.customizedAmount = amountForUI;
        }
    }
};
